import { useState } from "react";
import { QRCodeSVG } from "qrcode.react";
import { toast } from "sonner";
import {
  Phone, Mail, Globe, MapPin, FileText, UserPlus, Send, X, Loader2,
} from "lucide-react";
import { IconLinkedIn, IconWhatsApp, IconInstagram, IconFacebook, IconTikTok, IconYouTube, IconGoogle } from "@/components/site/BrandIcons";
import { Logo } from "@/components/site/Logo";
import { useI18n } from "@/i18n/I18nContext";

const withProto = (u) => (u && !/^https?:\/\//i.test(u) ? `https://${u}` : u);
const digits = (n) => (n || "").replace(/[^0-9+]/g, "");

export function buildVCard(d) {
  const fn = `${d.first_name || ""} ${d.last_name || ""}`.trim();
  const lines = ["BEGIN:VCARD", "VERSION:3.0", `N:${d.last_name || ""};${d.first_name || ""};;;`, `FN:${fn || "Contact"}`];
  if (d.company_name) lines.push(`ORG:${d.company_name}`);
  if (d.job_title) lines.push(`TITLE:${d.job_title}`);
  if (d.phone) lines.push(`TEL;TYPE=CELL:${d.phone}`);
  if (d.email) lines.push(`EMAIL:${d.email}`);
  if (d.website) lines.push(`URL:${d.website}`);
  if (d.address) lines.push(`ADR;TYPE=WORK:;;${d.address};;;;`);
  lines.push("END:VCARD");
  return URL.createObjectURL(new Blob([lines.join("\r\n")], { type: "text/vcard" }));
}

function Row({ icon: Icon, label, value, href, color, testid }) {
  if (!value) return null;
  return (
    <a href={href} target={href?.startsWith("http") ? "_blank" : undefined} rel="noopener noreferrer" data-testid={testid}
      className="flex items-center gap-3.5 px-4 py-3 rounded-2xl bg-slate-50 border border-slate-200 hover:bg-slate-100 transition-colors duration-300">
      <span className="w-9 h-9 rounded-xl flex items-center justify-center shrink-0" style={{ background: `${color}22`, color }}>
        <Icon className="w-4 h-4" />
      </span>
      <span className="min-w-0">
        <span className="block text-[10px] uppercase tracking-wide text-slate-400">{label}</span>
        <span className="block text-slate-900 text-sm truncate">{value}</span>
      </span>
    </a>
  );
}

function Soc({ icon: Icon, href, label, testid }) {
  if (!href) return null;
  return (
    <a href={withProto(href)} target="_blank" rel="noopener noreferrer" aria-label={label} data-testid={testid}
      className="w-11 h-11 rounded-full bg-slate-100 flex items-center justify-center text-slate-700 hover:bg-slate-200 hover:-translate-y-0.5 transition-all duration-300">
      <Icon className="w-5 h-5" />
    </a>
  );
}

function DemoContactModal({ primary, onClose }) {
  const { t } = useI18n();
  const [form, setForm] = useState({ first_name: "", last_name: "", phone: "", email: "", message: "", consent: false });
  const [loading, setLoading] = useState(false);
  const set = (k) => (e) => setForm((f) => ({ ...f, [k]: e.target.type === "checkbox" ? e.target.checked : e.target.value }));
  const submit = (e) => {
    e.preventDefault();
    if (!form.consent) return toast.error(t("fiche.form.consentError"));
    setLoading(true);
    setTimeout(() => { setLoading(false); toast.success(t("demo.sendDemoInfo")); onClose(); }, 500);
  };
  const inputCls = "w-full bg-slate-50 border border-slate-200 rounded-xl px-4 py-3 text-slate-900 placeholder:text-slate-400 focus:outline-none focus:ring-2 focus:ring-slate-300";
  return (
    <div className="fixed inset-0 z-[130] bg-black/50 backdrop-blur-sm flex items-center justify-center p-4" data-testid="demo-contact-modal">
      <div className="bg-white rounded-3xl w-full max-w-md max-h-[92vh] overflow-y-auto shadow-2xl">
        <div className="flex items-center justify-between px-6 py-4 border-b border-slate-100 sticky top-0 bg-white">
          <h3 className="font-display font-bold text-lg text-slate-900">{t("fiche.form.title")}</h3>
          <button onClick={onClose} className="text-slate-400 hover:text-slate-900" data-testid="demo-contact-close"><X className="w-5 h-5" /></button>
        </div>
        <form onSubmit={submit} className="p-6 space-y-3.5">
          <div className="grid grid-cols-2 gap-3">
            <input required placeholder={t("fiche.form.firstName")} value={form.first_name} onChange={set("first_name")} className={inputCls} />
            <input required placeholder={t("fiche.form.lastName")} value={form.last_name} onChange={set("last_name")} className={inputCls} />
          </div>
          <input required placeholder={t("fiche.form.phone")} value={form.phone} onChange={set("phone")} className={inputCls} />
          <input required type="email" placeholder={t("fiche.form.email")} value={form.email} onChange={set("email")} className={inputCls} />
          <textarea placeholder={t("fiche.form.message")} rows={3} value={form.message} onChange={set("message")} className={inputCls} />
          <label className="flex items-start gap-3 text-sm text-slate-500 cursor-pointer">
            <input type="checkbox" checked={form.consent} onChange={set("consent")} className="mt-1 accent-cyan-500 w-4 h-4" />
            <span>{t("fiche.form.consent")}</span>
          </label>
          <button type="submit" disabled={loading} data-testid="demo-contact-submit"
            className="w-full text-white font-semibold py-3 rounded-xl flex items-center justify-center gap-2 disabled:opacity-60" style={{ background: primary }}>
            {loading ? <Loader2 className="w-4 h-4 animate-spin" /> : <Send className="w-4 h-4" />} {t("fiche.form.submit")}
          </button>
        </form>
      </div>
    </div>
  );
}

export function FichePreview({ d }) {
  const { t } = useI18n();
  const [showContact, setShowContact] = useState(false);
  const primary = d.primary_color || "#06B6D4";
  const border = d.border_color || "#2563EB";
  const fullName = `${d.first_name || ""} ${d.last_name || ""}`.trim();
  const hasContact = d.phone || d.email || d.website || d.address;
  const hasSocial = d.linkedin || d.whatsapp || d.instagram || d.facebook || d.tiktok || d.youtube;

  const addContact = () => {
    const url = buildVCard(d);
    const a = document.createElement("a");
    a.href = url; a.download = `${fullName || "contact"}.vcf`;
    document.body.appendChild(a); a.click(); a.remove();
  };

  return (
    <div className="bg-white" data-testid="demo-fiche-preview" style={{ border: `1.5px solid ${border}` }}>
      <div className="relative h-32" style={{ background: d.cover_url ? undefined : `linear-gradient(120deg, ${primary}, ${border})` }}>
        {d.cover_url && <img src={d.cover_url} alt="" className="w-full h-full object-cover" />}
        <div className="absolute inset-0 bg-gradient-to-b from-transparent to-white" />
      </div>

      <div className="bg-white px-5 pb-7 -mt-12 relative">
        <div className="w-24 h-24 rounded-full mx-auto ring-4 ring-white shadow-md overflow-hidden flex items-center justify-center" style={{ background: `${primary}22` }} data-testid="dp-avatar">
          {d.profile_photo_url
            ? <img src={d.profile_photo_url} alt={fullName} className="w-full h-full object-cover" />
            : <span className="font-display font-bold text-2xl" style={{ color: primary }}>{(d.first_name?.[0] || "") + (d.last_name?.[0] || "")}</span>}
        </div>

        <div className="text-center mt-3">
          {fullName && <h3 className="font-display font-bold text-xl text-slate-900" data-testid="dp-name">{fullName}</h3>}
          {d.job_title && <p className="text-sm mt-0.5 font-medium" style={{ color: primary }} data-testid="dp-job">{d.job_title}</p>}
          {d.company_name && <p className="text-sm text-slate-500 mt-0.5" data-testid="dp-company">{d.company_name}</p>}
          {d.description && <p className="text-sm text-slate-500 mt-3 leading-relaxed" data-testid="dp-description">{d.description}</p>}
        </div>

        <div className="mt-5 space-y-2.5">
          <button onClick={addContact} data-testid="dp-add-contact"
            className="w-full text-white font-semibold py-3 rounded-2xl flex items-center justify-center gap-2 hover:opacity-90 transition-opacity duration-300" style={{ background: primary }}>
            <UserPlus className="w-5 h-5" /> {t("fiche.addContact")}
          </button>
          <button onClick={() => setShowContact(true)} data-testid="dp-send-coords"
            className="w-full bg-white border border-slate-200 text-slate-900 font-semibold py-3 rounded-2xl flex items-center justify-center gap-2 hover:bg-slate-50 transition-colors duration-300">
            <Send className="w-5 h-5" /> {t("fiche.sendCoords")}
          </button>
        </div>

        {hasContact && (
          <div className="mt-5 space-y-2.5" data-testid="dp-contacts">
            <Row icon={Phone} label={t("fiche.phone")} value={d.phone} href={`tel:${digits(d.phone)}`} color={primary} testid="dp-phone" />
            <Row icon={Mail} label={t("fiche.email")} value={d.email} href={`mailto:${d.email}`} color={primary} testid="dp-email" />
            <Row icon={Globe} label={t("fiche.website")} value={d.website} href={withProto(d.website)} color={primary} testid="dp-website" />
            <Row icon={MapPin} label={t("fiche.address")} value={d.address} href={`https://www.google.com/maps/search/?api=1&query=${encodeURIComponent(d.address || "")}`} color={primary} testid="dp-address" />
          </div>
        )}

        {hasSocial && (
          <div className="mt-5 flex flex-wrap justify-center gap-2.5" data-testid="dp-socials">
            <Soc icon={IconLinkedIn} href={d.linkedin} label="LinkedIn" testid="dp-linkedin" />
            <Soc icon={IconWhatsApp} href={d.whatsapp ? `https://wa.me/${(d.whatsapp || "").replace(/[^0-9]/g, "")}` : ""} label="WhatsApp" testid="dp-whatsapp" />
            <Soc icon={IconInstagram} href={d.instagram} label="Instagram" testid="dp-instagram" />
            <Soc icon={IconFacebook} href={d.facebook} label="Facebook" testid="dp-facebook" />
            <Soc icon={IconTikTok} href={d.tiktok} label="TikTok" testid="dp-tiktok" />
            <Soc icon={IconYouTube} href={d.youtube} label="YouTube" testid="dp-youtube" />
          </div>
        )}

        {(d.google_reviews || d.pdf_url) && (
          <div className="mt-5 space-y-2.5">
            <Row icon={IconGoogle} label={t("fiche.reviews")} value={d.google_reviews ? t("fiche.reviewsAction") : ""} href={withProto(d.google_reviews)} color={primary} testid="dp-reviews" />
            <Row icon={FileText} label={t("fiche.pdf")} value={d.pdf_url ? t("fiche.pdfAction") : ""} href={d.pdf_url || undefined} color={primary} testid="dp-pdf" />
          </div>
        )}

        <div className="mt-6 flex flex-col items-center">
          <div className="p-3 rounded-2xl bg-white border border-slate-200 shadow-sm"><QRCodeSVG value="https://bbrgo.fr" size={104} bgColor="#ffffff" fgColor="#0a0a12" level="M" /></div>
          <p className="mt-2.5 text-xs text-slate-400">{t("fiche.scanToOpen")}</p>
        </div>

        <div className="mt-6 pt-5 border-t border-slate-200 flex items-center justify-center">
          <Logo size="text-sm" dark />
        </div>
      </div>

      {showContact && <DemoContactModal primary={primary} onClose={() => setShowContact(false)} />}
    </div>
  );
}
